import React, { useRef, useState } from 'react';
import { Upload, FileText, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { resolveImage } from './ImageUpload';
import { useUploadManager } from '../../context/UploadManagerContext';

/**
 * Small admin widget to upload/remove a PDF (or other document) datasheet.
 * Delegates to the background upload manager so a slow upload survives
 * admin-panel navigation.
 */
const DatasheetUpload = ({ src, onChange }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const mgr = useUploadManager();

  const filename = src ? decodeURIComponent(src.split('/').pop()) : null;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File too large (max 20MB)');
      return;
    }
    setUploading(true);
    try {
      const { promise } = mgr.upload(file, { endpoint: '/admin/upload-document', label: file.name, kind: 'document' });
      const data = await promise;
      if (data?.url) onChange?.(data.url);
    } catch {
      /* tray + toast surface the error */
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const clear = () => {
    onChange?.(null);
    toast.success('Datasheet removed');
  };

  return (
    <div className="border border-dashed border-neutral-300 rounded-sm p-2 bg-neutral-50">
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
        className="hidden"
        onChange={handleFile}
        data-testid="datasheet-file-input"
      />
      {src ? (
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#F46A25] flex-shrink-0" />
          <a
            href={resolveImage(src)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 text-xs font-semibold text-[#12263A] truncate hover:text-[#F46A25]"
            title={filename}
          >
            {filename}
          </a>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="text-[10px] uppercase font-bold text-[#F46A25] hover:underline"
          >
            Replace
          </button>
          <button
            type="button"
            onClick={clear}
            aria-label="Remove datasheet"
            className="text-neutral-400 hover:text-red-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-full flex items-center justify-center gap-2 text-xs font-semibold text-neutral-600 hover:text-[#F46A25] py-1.5 disabled:opacity-50"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {uploading ? 'Uploading…' : 'Upload PDF datasheet'}
        </button>
      )}
    </div>
  );
};

export default DatasheetUpload;
